import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Stack,
  alpha,
  useTheme,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
} from "@mui/material";
import {
  ArrowLeft,
  Phone,
  Clock,
  Calendar,
  Zap,
  Signal,
  Filter,
  Download,
} from "lucide-react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";

const ClientDetailedLogs = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [filterType, setFilterType] = useState("all"); // 'all', 'sip', 'gsm'

  const fetchLogs = async (type = filterType) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${apiEndPointsConfig.getClientDetailedCallLogs}/${clientId}`, {
        params: { type: type === "all" ? undefined : type },
      });
      if (response.data.success) {
        setLogs(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching detailed logs:", error);
      alert("Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [clientId]);

  const handleTypeChange = (type) => {
    setFilterType(type);
    fetchLogs(type);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate("/admin/details")} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
          <ArrowLeft size={20} />
        </IconButton>
        <Box>
          <Breadcrumbs aria-label="breadcrumb">
            <MuiLink component={Link} underline="hover" color="inherit" to="/admin/details">
              Client Details
            </MuiLink>
            <Typography color="text.primary">Bifurcation</Typography>
          </Breadcrumbs>
          <Typography variant="h5" fontWeight={700}>
            Detailed Call Logs
          </Typography>
        </Box>
      </Stack>

      {/* Type Filter Tabs */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant={filterType === "all" ? "contained" : "outlined"}
            onClick={() => handleTypeChange("all")}
            startIcon={<Phone size={18} />}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            All Calls
          </Button>
          <Button
            variant={filterType === "sip" ? "contained" : "outlined"}
            onClick={() => handleTypeChange("sip")}
            startIcon={<Zap size={18} />}
            color="info"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            SIP Calls (04)
          </Button>
          <Button
            variant={filterType === "gsm" ? "contained" : "outlined"}
            onClick={() => handleTypeChange("gsm")}
            startIcon={<Signal size={18} />}
            color="warning"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            GSM Calls (05)
          </Button>
        </Stack>
      </Paper>

      {/* Logs Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Call ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>DID Number</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Direction</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duration (Sec)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length > 0 ? (
                logs.map((log) => {
                  const did = log.payload?.didNumber || "";
                  const isSip = did.startsWith("04") || did.startsWith("4");
                  const isGsm = did.startsWith("05") || did.startsWith("5");
                  
                  return (
                    <TableRow key={log._id} sx={{ "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}>
                      <TableCell>{log.payload?.callId || "N/A"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {log.payload?.callDate}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.payload?.calls_time}
                        </Typography>
                      </TableCell>
                      <TableCell>{did}</TableCell>
                      <TableCell>{log.payload?.mobile}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.payload?.calls_direction}
                          size="small"
                          color={log.payload?.calls_direction === "Inbound" ? "secondary" : "primary"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={(log.payload?.status || "").toLowerCase().includes("not connected") ? "error.main" : "success.main"}
                        >
                          {log.payload?.status}
                        </Typography>
                      </TableCell>
                      <TableCell>{log.payload?.timeDuration}s</TableCell>
                      <TableCell>
                        {isSip ? (
                          <Chip label="SIP" size="small" icon={<Zap size={14} />} sx={{ bgcolor: alpha("#06B6D4", 0.1), color: "#06B6D4" }} />
                        ) : isGsm ? (
                          <Chip label="GSM" size="small" icon={<Signal size={14} />} sx={{ bgcolor: alpha("#F97316", 0.1), color: "#F97316" }} />
                        ) : (
                          <Chip label="Other" size="small" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                    <Typography color="text.secondary">No detailed logs found for this client.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default ClientDetailedLogs;
